/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, memo, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import { useTranslation } from 'react-i18next';
import { truncateMarkdown } from '../../../../utils/string/truncateMarkdown';

// Component imports
import { Space, Typography } from 'antd';
import NodeWrapper from '../../NodeWrapper';
import { GraphContext } from '../../FlowGraph';
import { GRAPH_LAYOUTS, NODE_TYPES_PROPERTIES, USER_INPUT_QUERY_HANDLE_IDS } from '../../../../constants/flowGraph';
import LimitedConnectionsHandle from '../../LimitedConnectionsHandle';
import { CloseOutlined } from '@ant-design/icons';

// Misc imports
import { useBotLanguage } from '../../../../utils/hooks/useBotLanguage';

// Constant declarations
const { Text } = Typography;

const UserInputQueryNode = ({ id, type, data, dragging, selected }) => {
  const nodeProps = NODE_TYPES_PROPERTIES[type];
  const { t } = useTranslation();
  const { currentBotLanguage } = useBotLanguage();
  const { graphDirection } = useContext(GraphContext);

  const NodeContents = useMemo(() => {
    return (
      <Fragment>
        <LimitedConnectionsHandle type="target" position={graphDirection === GRAPH_LAYOUTS.VERTICAL ? 'top' : 'left'}/>
        <Space size={6} className="node-header">
          {nodeProps.icon}
          {nodeProps.label}
        </Space>
        {
          data.label[currentBotLanguage] ? (
            <Text >
              <ReactMarkdown
                skipHtml
                linkTarget="_blank"
              >
                {
                  truncateMarkdown(data.label[currentBotLanguage], {
                    limit: 50,
                    ellipsis: true,
                    replaceImages: true,
                    replaceBlankSpaces: true
                  })
                }
              </ReactMarkdown>
            </Text>
          ) : (
            <Text type="secondary">
              {t('Click to edit')}
            </Text>
          )
        }
        <LimitedConnectionsHandle
          type="source"
          id={USER_INPUT_QUERY_HANDLE_IDS.FALSE}
          source={id}
          position={graphDirection === GRAPH_LAYOUTS.VERTICAL ? 'right' : 'bottom'}
          allowedOutgoingEdgesCount={1}
          icon={<CloseOutlined className="fs-xs"/>}
        />
        <LimitedConnectionsHandle
          type="source"
          id={USER_INPUT_QUERY_HANDLE_IDS.TRUE}
          source={id}
          position={graphDirection === GRAPH_LAYOUTS.VERTICAL ? 'bottom' : 'right'}
          allowedOutgoingEdgesCount={1}
        />
      </Fragment>
    );
  }, [data, nodeProps, graphDirection, currentBotLanguage]);

  return (
    <NodeWrapper
      id={id}
      type={type}
      data={data}
      children={NodeContents}
      dragging={dragging}
      selected={selected}
    />
  );
};

// https://reactflow.dev/docs/api/nodes/node-options/
UserInputQueryNode.propTypes = {
  data: PropTypes.object,
  id: PropTypes.string,
  dragging: PropTypes.bool,
  selected: PropTypes.bool,
  type: PropTypes.string,
  xPos: PropTypes.number,
  yPos: PropTypes.number
};

export default memo(UserInputQueryNode);
